import { useState, useMemo, useId } from 'react';
import {
  Plus,
  ChevronDown,
  Loader2,
  Eye,
  CheckCircle2,
  XCircle,
  Users,
} from 'lucide-react';
import { format, parseISO } from 'date-fns';
import { cn } from '@/lib/utils';
import { extractErrorMessage } from '@/lib/error';
import { PageHeader } from '@/components/ui/PageHeader';
import { SlideOver } from '@/components/ui/SlideOver';
import { EmptyState } from '@/components/ui/EmptyState';
import { Button } from '@/components/ui/Button';
import { useSelectedPropertyId } from '@/contexts/PropertyContext';
import { useUnits } from '@/hooks/api/useProperties';
import { useCompanies } from '@/hooks/api/useCompanies';
import {
  useGroupBookings,
  useGroupBooking,
  useCreateGroupBooking,
  useConfirmGroupBooking,
  useCancelGroupBooking,
} from '@/hooks/api/useGroups';
import type { GroupBooking } from '@/services/api/groups.api';

// ─── Constants ────────────────────────────────────────────────────────────────

type GroupStatus = GroupBooking['status'];

const STATUS_STYLES: Record<GroupStatus, string> = {
  tentative: 'bg-yellow-100 text-yellow-700',
  confirmed: 'bg-blue-100 text-blue-700',
  checked_in: 'bg-green-100 text-green-700',
  completed: 'bg-gray-100 text-gray-700',
  cancelled: 'bg-red-100 text-red-700',
};

const STATUS_LABELS: Record<GroupStatus, string> = {
  tentative: 'Tentative',
  confirmed: 'Confirmed',
  checked_in: 'Checked In',
  completed: 'Completed',
  cancelled: 'Cancelled',
};

const STATUS_FILTER_OPTIONS: Array<{ value: string; label: string }> = [
  { value: '', label: 'All Statuses' },
  { value: 'tentative', label: 'Tentative' },
  { value: 'confirmed', label: 'Confirmed' },
  { value: 'checked_in', label: 'Checked In' },
  { value: 'completed', label: 'Completed' },
  { value: 'cancelled', label: 'Cancelled' },
];

// ─── Helpers ──────────────────────────────────────────────────────────────────

function fmtDate(iso: string): string {
  try {
    return format(parseISO(iso), 'MMM d, yyyy');
  } catch {
    return iso;
  }
}

function fmtCurrency(amount: string, currency = 'USD'): string {
  try {
    const num = parseFloat(amount);
    if (isNaN(num)) return amount;
    return new Intl.NumberFormat('en-US', { style: 'currency', currency }).format(num);
  } catch {
    return `$${amount}`;
  }
}

// ─── Stats Card ───────────────────────────────────────────────────────────────

function StatCard({
  label,
  value,
  colorClass,
}: {
  label: string;
  value: number;
  colorClass: string;
}) {
  return (
    <div className="bg-white rounded-xl border border-gray-200 shadow-sm px-5 py-4">
      <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide">{label}</p>
      <p className={cn('text-2xl font-bold mt-1', colorClass)}>{value}</p>
    </div>
  );
}

// ─── Create Group SlideOver ───────────────────────────────────────────────────

interface CreateGroupFormData {
  name: string;
  companyId: string;
  checkIn: string;
  checkOut: string;
  notes: string;
  selectedUnits: Record<string, number>; // unitId -> guestsCount
}

const EMPTY_FORM: CreateGroupFormData = {
  name: '',
  companyId: '',
  checkIn: '',
  checkOut: '',
  notes: '',
  selectedUnits: {},
};

function CreateGroupSlideOver({
  isOpen,
  onClose,
  propertyId,
}: {
  isOpen: boolean;
  onClose: () => void;
  propertyId: string;
}) {
  const [form, setForm] = useState<CreateGroupFormData>({ ...EMPTY_FORM });
  const [errors, setErrors] = useState<Partial<Record<string, string>>>({});

  const nameInputId = useId();
  const companySelectId = useId();
  const checkInId = useId();
  const checkOutId = useId();
  const notesId = useId();

  const { data: unitsData, isLoading: unitsLoading } = useUnits(propertyId || undefined);
  const { data: companiesData } = useCompanies();
  const createMutation = useCreateGroupBooking();

  const units: Array<{ id: string; name: string; type: string }> = useMemo(() => {
    if (!unitsData) return [];
    const raw = Array.isArray(unitsData) ? unitsData : ((unitsData as Record<string, unknown>).data as unknown[] ?? []);
    return raw.map((u: unknown) => {
      const unit = u as Record<string, unknown>;
      return {
        id: (unit.id as string) ?? '',
        name: (unit.name as string) ?? '',
        type: (unit.type as string) ?? '',
      };
    });
  }, [unitsData]);

  const companies: Array<{ id: string; name: string }> = useMemo(() => {
    if (!companiesData) return [];
    const raw = Array.isArray(companiesData)
      ? companiesData
      : ((companiesData as unknown as Record<string, unknown>).data as unknown[] ?? []);
    return raw.map((c: unknown) => {
      const company = c as Record<string, unknown>;
      return {
        id: (company.id as string) ?? '',
        name: (company.name as string) ?? '',
      };
    });
  }, [companiesData]);

  function clearMutationError() {
    if (createMutation.isError) createMutation.reset();
  }

  function toggleUnit(unitId: string) {
    setForm((prev) => {
      const next = { ...prev.selectedUnits };
      if (next[unitId] !== undefined) {
        delete next[unitId];
      } else {
        next[unitId] = 1;
      }
      return { ...prev, selectedUnits: next };
    });
    if (errors.units) setErrors((prev) => ({ ...prev, units: undefined }));
    clearMutationError();
  }

  function setGuestsCount(unitId: string, count: number) {
    setForm((prev) => ({
      ...prev,
      selectedUnits: { ...prev.selectedUnits, [unitId]: Math.max(1, count) },
    }));
  }

  function validate(): boolean {
    const next: Record<string, string> = {};
    if (!form.name.trim()) next.name = 'Group name is required';
    if (!form.checkIn) next.checkIn = 'Check-in date is required';
    if (!form.checkOut) next.checkOut = 'Check-out date is required';
    if (form.checkIn && form.checkOut && form.checkIn >= form.checkOut) {
      next.checkOut = 'Check-out must be after check-in';
    }
    if (Object.keys(form.selectedUnits).length === 0) {
      next.units = 'Select at least one unit';
    }
    setErrors(next);
    return Object.keys(next).length === 0;
  }

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!validate()) return;

    createMutation.mutate(
      {
        name: form.name.trim(),
        propertyId,
        checkIn: form.checkIn,
        checkOut: form.checkOut,
        units: Object.entries(form.selectedUnits).map(([unitId, guestsCount]) => ({
          unitId,
          guestsCount,
        })),
        ...(form.companyId ? { companyId: form.companyId } : {}),
        ...(form.notes.trim() ? { notes: form.notes.trim() } : {}),
      },
      {
        onSuccess: () => {
          setForm({ ...EMPTY_FORM });
          setErrors({});
          onClose();
        },
      },
    );
  }

  function handleClose() {
    setForm({ ...EMPTY_FORM });
    setErrors({});
    onClose();
  }

  return (
    <SlideOver isOpen={isOpen} onClose={handleClose} title="New Group Booking" width="max-w-lg">
      <form onSubmit={handleSubmit} className="space-y-4">
        {/* Group name */}
        <div>
          <label
            htmlFor={nameInputId}
            className="block text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1.5"
          >
            Group Name <span aria-label="required">*</span>
          </label>
          <input
            id={nameInputId}
            type="text"
            value={form.name}
            onChange={(e) => {
              setForm((prev) => ({ ...prev, name: e.target.value }));
              if (errors.name) setErrors((prev) => ({ ...prev, name: undefined }));
              clearMutationError();
            }}
            placeholder="Corporate retreat, Wedding block..."
            required
            aria-invalid={!!errors.name}
            aria-describedby={errors.name ? `${nameInputId}-error` : undefined}
            className={cn(
              'w-full rounded-lg border px-3 py-2 text-sm text-gray-800 placeholder:text-gray-400 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500',
              errors.name ? 'border-red-400' : 'border-gray-300',
            )}
          />
          {errors.name && (
            <p id={`${nameInputId}-error`} className="mt-1 text-xs text-red-600" role="alert">
              {errors.name}
            </p>
          )}
        </div>

        {/* Company */}
        <div>
          <label
            htmlFor={companySelectId}
            className="block text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1.5"
          >
            Company
          </label>
          <div className="relative">
            <select
              id={companySelectId}
              value={form.companyId}
              onChange={(e) => setForm((prev) => ({ ...prev, companyId: e.target.value }))}
              className="w-full appearance-none rounded-lg border border-gray-300 px-3 py-2 pr-8 text-sm text-gray-800 bg-white focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
            >
              <option value="">No company (optional)</option>
              {companies.map((c) => (
                <option key={c.id} value={c.id}>
                  {c.name}
                </option>
              ))}
            </select>
            <ChevronDown
              size={14}
              className="absolute right-2.5 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none"
              aria-hidden="true"
            />
          </div>
        </div>

        {/* Dates */}
        <div className="grid grid-cols-2 gap-3">
          <div>
            <label
              htmlFor={checkInId}
              className="block text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1.5"
            >
              Check-in <span aria-label="required">*</span>
            </label>
            <input
              id={checkInId}
              type="date"
              value={form.checkIn}
              onChange={(e) => {
                setForm((prev) => ({ ...prev, checkIn: e.target.value }));
                if (errors.checkIn) setErrors((prev) => ({ ...prev, checkIn: undefined }));
                clearMutationError();
              }}
              required
              aria-invalid={!!errors.checkIn}
              className={cn(
                'w-full rounded-lg border px-3 py-2 text-sm text-gray-800 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500',
                errors.checkIn ? 'border-red-400' : 'border-gray-300',
              )}
            />
            {errors.checkIn && (
              <p className="mt-1 text-xs text-red-600" role="alert">{errors.checkIn}</p>
            )}
          </div>
          <div>
            <label
              htmlFor={checkOutId}
              className="block text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1.5"
            >
              Check-out <span aria-label="required">*</span>
            </label>
            <input
              id={checkOutId}
              type="date"
              value={form.checkOut}
              onChange={(e) => {
                setForm((prev) => ({ ...prev, checkOut: e.target.value }));
                if (errors.checkOut) setErrors((prev) => ({ ...prev, checkOut: undefined }));
                clearMutationError();
              }}
              required
              min={form.checkIn || undefined}
              aria-invalid={!!errors.checkOut}
              className={cn(
                'w-full rounded-lg border px-3 py-2 text-sm text-gray-800 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500',
                errors.checkOut ? 'border-red-400' : 'border-gray-300',
              )}
            />
            {errors.checkOut && (
              <p className="mt-1 text-xs text-red-600" role="alert">{errors.checkOut}</p>
            )}
          </div>
        </div>

        {/* Unit selection */}
        <div>
          <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">
            Units <span aria-label="required">*</span>
          </p>
          {unitsLoading ? (
            <div className="flex items-center gap-2 py-4 text-sm text-gray-500">
              <Loader2 size={16} className="animate-spin" aria-hidden="true" />
              Loading units...
            </div>
          ) : units.length === 0 ? (
            <p className="text-sm text-gray-400 py-2">No units available for this property.</p>
          ) : (
            <fieldset>
              <legend className="sr-only">Select units for the group booking</legend>
              <div className={cn(
                'space-y-2 max-h-48 overflow-y-auto border rounded-lg p-3',
                errors.units ? 'border-red-400' : 'border-gray-200',
              )}>
                {units.map((unit) => {
                  const isSelected = form.selectedUnits[unit.id] !== undefined;
                  return (
                    <div key={unit.id} className="flex items-center gap-3">
                      <label className="flex items-center gap-2 flex-1 cursor-pointer">
                        <input
                          type="checkbox"
                          checked={isSelected}
                          onChange={() => toggleUnit(unit.id)}
                          className="rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                        />
                        <span className="text-sm text-gray-800">
                          {unit.name}
                          {unit.type && (
                            <span className="text-gray-400 ml-1">({unit.type})</span>
                          )}
                        </span>
                      </label>
                      {isSelected && (
                        <label className="flex items-center gap-1 shrink-0">
                          <span className="text-xs text-gray-500">Guests:</span>
                          <input
                            type="number"
                            min={1}
                            max={20}
                            value={form.selectedUnits[unit.id]}
                            onChange={(e) =>
                              setGuestsCount(unit.id, parseInt(e.target.value, 10) || 1)
                            }
                            className="w-14 rounded border border-gray-300 px-2 py-1 text-sm text-gray-800 focus:outline-none focus:ring-2 focus:ring-primary-500"
                            aria-label={`Guests count for ${unit.name}`}
                          />
                        </label>
                      )}
                    </div>
                  );
                })}
              </div>
            </fieldset>
          )}
          {errors.units && (
            <p className="mt-1 text-xs text-red-600" role="alert">{errors.units}</p>
          )}
          {!errors.units && (
            <p className="mt-1 text-xs text-gray-400">
              {Object.keys(form.selectedUnits).length} unit{Object.keys(form.selectedUnits).length !== 1 ? 's' : ''} selected
            </p>
          )}
        </div>

        {/* Notes */}
        <div>
          <label
            htmlFor={notesId}
            className="block text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1.5"
          >
            Notes
          </label>
          <textarea
            id={notesId}
            rows={3}
            value={form.notes}
            onChange={(e) => setForm((prev) => ({ ...prev, notes: e.target.value }))}
            placeholder="Group-specific notes..."
            className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm text-gray-800 placeholder:text-gray-400 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500 resize-none"
          />
        </div>

        {createMutation.isError && (
          <p className="text-sm text-red-600 bg-red-50 border border-red-200 rounded-lg px-3 py-2" role="alert">
            {extractErrorMessage(createMutation.error)}
          </p>
        )}

        <div className="pt-2">
          <Button
            type="submit"
            fullWidth
            isLoading={createMutation.isPending}
            disabled={createMutation.isPending}
          >
            Create Group
          </Button>
        </div>
      </form>
    </SlideOver>
  );
}

// ─── Group Detail SlideOver ───────────────────────────────────────────────────

function GroupDetailSlideOver({
  groupId,
  onClose,
}: {
  groupId: string | null;
  onClose: () => void;
}) {
  const { data: group, isLoading } = useGroupBooking(groupId);
  const confirmMutation = useConfirmGroupBooking();
  const cancelMutation = useCancelGroupBooking();

  if (!groupId) return null;

  return (
    <SlideOver isOpen={!!groupId} onClose={onClose} title="Group Details" width="max-w-xl">
      {isLoading ? (
        <div className="flex items-center justify-center py-16">
          <Loader2 size={24} className="animate-spin text-primary-600" aria-hidden="true" />
          <span className="ml-2 text-sm text-gray-500">Loading group details...</span>
        </div>
      ) : !group ? (
        <p className="text-sm text-gray-400 text-center py-8">Group not found.</p>
      ) : (
        <div className="space-y-6">
          {/* Header info */}
          <div>
            <div className="flex items-center gap-3 mb-2">
              <h3 className="text-lg font-semibold text-gray-900">{group.name}</h3>
              <span
                className={cn(
                  'inline-flex items-center rounded-full px-3 py-1 text-xs font-medium',
                  STATUS_STYLES[group.status],
                )}
              >
                {STATUS_LABELS[group.status]}
              </span>
            </div>
            <dl className="grid grid-cols-2 gap-x-4 gap-y-2 text-sm">
              <div>
                <dt className="text-gray-500">Check-in</dt>
                <dd className="font-medium text-gray-900">{fmtDate(group.checkIn)}</dd>
              </div>
              <div>
                <dt className="text-gray-500">Check-out</dt>
                <dd className="font-medium text-gray-900">{fmtDate(group.checkOut)}</dd>
              </div>
              <div>
                <dt className="text-gray-500">Company</dt>
                <dd className="font-medium text-gray-900">
                  {group.company?.name ?? '\u2014'}
                </dd>
              </div>
              <div>
                <dt className="text-gray-500">Contact</dt>
                <dd className="font-medium text-gray-900">
                  {group.contactGuest?.fullName ?? '\u2014'}
                </dd>
              </div>
              <div>
                <dt className="text-gray-500">Units</dt>
                <dd className="font-medium text-gray-900">{group.totalUnits}</dd>
              </div>
              <div>
                <dt className="text-gray-500">Total</dt>
                <dd className="font-medium text-gray-900">
                  {fmtCurrency(group.totalAmount, group.currency)}
                </dd>
              </div>
            </dl>
          </div>

          {/* Notes */}
          {group.notes && (
            <div>
              <h4 className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1">
                Notes
              </h4>
              <p className="text-sm text-gray-700 bg-gray-50 rounded-lg p-3">{group.notes}</p>
            </div>
          )}

          {/* Individual bookings */}
          <div>
            <h4 className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">
              Individual Bookings ({group.bookings?.length ?? 0})
            </h4>
            {!group.bookings || group.bookings.length === 0 ? (
              <p className="text-sm text-gray-400">No individual bookings yet.</p>
            ) : (
              <div className="space-y-2">
                {group.bookings.map((booking) => (
                  <div
                    key={booking.id}
                    className="flex items-center justify-between bg-gray-50 rounded-lg px-4 py-3"
                  >
                    <div className="min-w-0">
                      <p className="text-sm font-medium text-gray-900 truncate">
                        {booking.unit?.name ?? 'Unit'}
                      </p>
                      <p className="text-xs text-gray-500">
                        {booking.guest?.fullName ?? 'No guest assigned'}
                        {booking.guestsCount > 0 && (
                          <span className="ml-2">
                            ({booking.guestsCount} guest{booking.guestsCount !== 1 ? 's' : ''})
                          </span>
                        )}
                      </p>
                    </div>
                    <div className="flex items-center gap-3 shrink-0">
                      <span className="text-sm font-semibold text-gray-900">
                        {fmtCurrency(booking.totalAmount)}
                      </span>
                      <span
                        className={cn(
                          'inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium',
                          STATUS_STYLES[booking.status as GroupStatus] ?? 'bg-gray-100 text-gray-600',
                        )}
                      >
                        {STATUS_LABELS[booking.status as GroupStatus] ?? booking.status}
                      </span>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Actions */}
          <div className="flex items-center gap-3 pt-2 border-t border-gray-100">
            {group.status === 'tentative' && (
              <Button
                variant="primary"
                size="sm"
                isLoading={confirmMutation.isPending}
                onClick={() =>
                  confirmMutation.mutate(group.id, {
                    onSuccess: () => onClose(),
                  })
                }
              >
                <CheckCircle2 size={14} aria-hidden="true" />
                Confirm All
              </Button>
            )}
            {group.status !== 'cancelled' && group.status !== 'completed' && (
              <Button
                variant="danger"
                size="sm"
                isLoading={cancelMutation.isPending}
                onClick={() =>
                  cancelMutation.mutate(
                    { id: group.id, reason: 'Cancelled by staff' },
                    { onSuccess: () => onClose() },
                  )
                }
              >
                <XCircle size={14} aria-hidden="true" />
                Cancel Group
              </Button>
            )}
          </div>
        </div>
      )}
    </SlideOver>
  );
}

// ─── Page ─────────────────────────────────────────────────────────────────────

export function GroupBookingsPage() {
  const propertyId = useSelectedPropertyId();
  const [statusFilter, setStatusFilter] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [page, setPage] = useState(1);
  const [isCreateOpen, setIsCreateOpen] = useState(false);
  const [detailGroupId, setDetailGroupId] = useState<string | null>(null);

  const statusSelectId = useId();

  const query = useMemo(
    () => ({
      ...(propertyId ? { propertyId } : {}),
      ...(statusFilter ? { status: statusFilter } : {}),
      page,
      limit: 20,
    }),
    [propertyId, statusFilter, page],
  );

  const { data, isLoading } = useGroupBookings(query);
  const confirmMutation = useConfirmGroupBooking();
  const cancelMutation = useCancelGroupBooking();

  const rawGroups: GroupBooking[] = useMemo(() => {
    if (!data) return [];
    return Array.isArray(data) ? data : (data.data ?? []);
  }, [data]);

  const allGroups: GroupBooking[] = useMemo(() => {
    if (!searchTerm) return rawGroups;
    const q = searchTerm.toLowerCase();
    return rawGroups.filter(
      (g) =>
        g.name.toLowerCase().includes(q) ||
        (g.company?.name ?? '').toLowerCase().includes(q),
    );
  }, [rawGroups, searchTerm]);

  const meta = data && !Array.isArray(data) ? data.meta : null;
  const totalPages = meta
    ? Math.ceil((meta.total ?? allGroups.length) / (meta.limit ?? 20))
    : 1;

  // Stats
  const stats = useMemo(() => {
    const tentative = allGroups.filter((g) => g.status === 'tentative').length;
    const confirmed = allGroups.filter((g) => g.status === 'confirmed').length;
    const active = allGroups.filter((g) => g.status === 'checked_in').length;
    return { tentative, confirmed, active, total: allGroups.length };
  }, [allGroups]);

  return (
    <div className="flex flex-col h-full space-y-4">
      <PageHeader
        title="Group Bookings"
        description="Manage group reservations and block bookings"
        actions={
          <Button onClick={() => setIsCreateOpen(true)}>
            <Plus size={16} aria-hidden="true" />
            New Group
          </Button>
        }
      />

      {/* Stats row */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <StatCard label="Tentative" value={stats.tentative} colorClass="text-yellow-600" />
        <StatCard label="Confirmed" value={stats.confirmed} colorClass="text-blue-600" />
        <StatCard label="Active" value={stats.active} colorClass="text-green-600" />
        <StatCard label="Total Groups" value={stats.total} colorClass="text-gray-900" />
      </div>

      {/* Filter bar */}
      <div className="flex items-center gap-3">
        <input
          type="text"
          placeholder="Search by name or company..."
          value={searchTerm}
          onChange={(e) => { setSearchTerm(e.target.value); setPage(1); }}
          className="rounded-lg border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 w-64"
          aria-label="Search group bookings"
        />
        <div className="relative">
          <label htmlFor={statusSelectId} className="sr-only">
            Filter by status
          </label>
          <select
            id={statusSelectId}
            value={statusFilter}
            onChange={(e) => {
              setStatusFilter(e.target.value);
              setPage(1);
            }}
            className="appearance-none rounded-lg border border-gray-300 bg-white px-3 py-2 pr-8 text-sm text-gray-800 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
          >
            {STATUS_FILTER_OPTIONS.map((opt) => (
              <option key={opt.value} value={opt.value}>
                {opt.label}
              </option>
            ))}
          </select>
          <ChevronDown
            size={14}
            className="absolute right-2.5 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none"
            aria-hidden="true"
          />
        </div>
        <p className="text-xs text-gray-500 ml-auto" aria-live="polite">
          {allGroups.length} group{allGroups.length !== 1 ? 's' : ''}
        </p>
      </div>

      {/* Groups table */}
      <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden">
        {isLoading ? (
          <div className="flex items-center justify-center py-16">
            <Loader2 size={24} className="animate-spin text-primary-600" aria-hidden="true" />
            <span className="ml-2 text-sm text-gray-500">Loading groups...</span>
          </div>
        ) : allGroups.length === 0 ? (
          <EmptyState
            icon={Users}
            title="No group bookings"
            description="Create your first group booking to manage block reservations."
            actionLabel="New Group"
            onAction={() => setIsCreateOpen(true)}
          />
        ) : (
          <>
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-100" aria-label="Group bookings list">
                <thead>
                  <tr className="bg-gray-50">
                    {['Group Name', 'Company', 'Check-in', 'Check-out', 'Units', 'Total Amount', 'Status', 'Actions'].map(
                      (col) => (
                        <th
                          key={col}
                          scope="col"
                          className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wide whitespace-nowrap"
                        >
                          {col}
                        </th>
                      ),
                    )}
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {allGroups.map((group) => (
                    <tr
                      key={group.id}
                      className="hover:bg-gray-50 transition-colors cursor-pointer"
                      onClick={() => setDetailGroupId(group.id)}
                    >
                      <td className="px-4 py-3.5">
                        <p className="text-sm font-medium text-gray-900 whitespace-nowrap">
                          {group.name}
                        </p>
                      </td>
                      <td className="px-4 py-3.5 text-sm text-gray-700 whitespace-nowrap">
                        {group.company?.name ?? '\u2014'}
                      </td>
                      <td className="px-4 py-3.5 text-sm text-gray-600 whitespace-nowrap">
                        {fmtDate(group.checkIn)}
                      </td>
                      <td className="px-4 py-3.5 text-sm text-gray-600 whitespace-nowrap">
                        {fmtDate(group.checkOut)}
                      </td>
                      <td className="px-4 py-3.5 text-sm text-gray-700 text-center">
                        {group.totalUnits}
                      </td>
                      <td className="px-4 py-3.5 text-sm font-semibold text-gray-900 whitespace-nowrap">
                        {fmtCurrency(group.totalAmount, group.currency)}
                      </td>
                      <td className="px-4 py-3.5 whitespace-nowrap">
                        <span
                          className={cn(
                            'inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium',
                            STATUS_STYLES[group.status],
                          )}
                        >
                          {STATUS_LABELS[group.status]}
                        </span>
                      </td>
                      <td className="px-4 py-3.5">
                        <div
                          className="flex items-center gap-1"
                          onClick={(e) => e.stopPropagation()}
                        >
                          <button
                            type="button"
                            onClick={() => setDetailGroupId(group.id)}
                            aria-label={`View details for ${group.name}`}
                            title="View"
                            className="inline-flex items-center gap-1 px-2 py-1 rounded-md bg-gray-50 text-gray-600 text-xs font-medium hover:bg-gray-100 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-500 transition-colors"
                          >
                            <Eye size={12} aria-hidden="true" />
                            View
                          </button>
                          {group.status === 'tentative' && (
                            <button
                              type="button"
                              onClick={() => confirmMutation.mutate(group.id)}
                              disabled={confirmMutation.isPending}
                              aria-label={`Confirm group ${group.name}`}
                              title="Confirm"
                              className="inline-flex items-center gap-1 px-2 py-1 rounded-md bg-blue-50 text-blue-700 text-xs font-medium hover:bg-blue-100 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 transition-colors"
                            >
                              <CheckCircle2 size={12} aria-hidden="true" />
                              Confirm
                            </button>
                          )}
                          {group.status !== 'cancelled' && group.status !== 'completed' && (
                            <button
                              type="button"
                              onClick={() =>
                                cancelMutation.mutate({
                                  id: group.id,
                                  reason: 'Cancelled by staff',
                                })
                              }
                              disabled={cancelMutation.isPending}
                              aria-label={`Cancel group ${group.name}`}
                              title="Cancel"
                              className="p-1.5 rounded-md text-gray-400 hover:text-red-600 hover:bg-red-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-red-400 transition-colors"
                            >
                              <XCircle size={14} aria-hidden="true" />
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Pagination */}
            {totalPages > 1 && (
              <div className="flex items-center justify-between px-4 py-3 border-t border-gray-100">
                <p className="text-xs text-gray-500">
                  Page {page} of {totalPages}
                </p>
                <div className="flex gap-2">
                  <button
                    onClick={() => setPage((p) => Math.max(1, p - 1))}
                    disabled={page <= 1}
                    className={cn(
                      'px-3 py-1.5 rounded-lg text-xs font-medium border transition-colors',
                      'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-500',
                      page <= 1
                        ? 'border-gray-200 text-gray-300 cursor-not-allowed'
                        : 'border-gray-300 text-gray-600 hover:bg-gray-50',
                    )}
                  >
                    Previous
                  </button>
                  <button
                    onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                    disabled={page >= totalPages}
                    className={cn(
                      'px-3 py-1.5 rounded-lg text-xs font-medium border transition-colors',
                      'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-500',
                      page >= totalPages
                        ? 'border-gray-200 text-gray-300 cursor-not-allowed'
                        : 'border-gray-300 text-gray-600 hover:bg-gray-50',
                    )}
                  >
                    Next
                  </button>
                </div>
              </div>
            )}
          </>
        )}
      </div>

      {/* SlideOvers */}
      <CreateGroupSlideOver
        isOpen={isCreateOpen}
        onClose={() => setIsCreateOpen(false)}
        propertyId={propertyId}
      />
      <GroupDetailSlideOver
        groupId={detailGroupId}
        onClose={() => setDetailGroupId(null)}
      />
    </div>
  );
}
